import { useLaunchToast } from '@/composables/useToast';
import type {
    OrderClientIsEditedNotification,
    OrderIsEditedNotification,
    SpecialCaseAddedNotification,
    SpecialCaseRemovedNotification,
} from '@/generated';
import type { AutomaticProcessingStatusNotification } from '@/models/automaticProcessingStatus';
import {
    getSignalRConnection,
    hasSignalRReconnectRetryLimitReached,
    resetSignalRReconnectRetryLimitReached,
    startSignalR,
    stopSignalR,
} from '@/services/signalr';
import useAuthStore from '@/stores/useAuthStore';
import useSignalRStore from '@/stores/useSignalRStore';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { storeToRefs } from 'pinia';
import { useToast } from 'primevue';
import { onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const METHOD_NAME = 'receiveNotification';

const subscribers = new Set<object>();
const signalRReconnectHandlers = new WeakSet<object>();
const signalRReconnectListeners = new WeakMap<HubConnection, Set<() => void>>();
const signalRFallbackHandlers = new WeakMap<
    HubConnection,
    Map<string, (...args: unknown[]) => void>
>();

const ensureFallbackHandler = (connection: HubConnection, methodName: string) => {
    let methodHandlers = signalRFallbackHandlers.get(connection);
    if (!methodHandlers) {
        methodHandlers = new Map<string, (...args: unknown[]) => void>();
        signalRFallbackHandlers.set(connection, methodHandlers);
    }

    if (methodHandlers.has(methodName)) {
        return;
    }

    const noopHandler = () => {
        // Intentionally empty: keeps SignalR quiet when no page-level listener is mounted.
    };

    methodHandlers.set(methodName, noopHandler);
    connection.on(methodName, noopHandler);
};

export interface NotificationTypeMap {
    OrderIsEditedNotification: OrderIsEditedNotification;
    OrderClientIsEditedNotification: OrderClientIsEditedNotification;
    SpecialCaseAddedNotification: SpecialCaseAddedNotification;
    SpecialCaseRemovedNotification: SpecialCaseRemovedNotification;
    CreditNoteProcessAutomatedStatusNotification: AutomaticProcessingStatusNotification;
}
export interface SignalRNotification {
    notificationType: (keyof NotificationTypeMap)[];
    onNotificationReceived: (arg: unknown) => void;
    onReconnected?: () => void;
}

export function useSignalRNotifications({
    notificationType,
    onNotificationReceived,
    onReconnected,
}: SignalRNotification) {
    const subscriber = {};
    subscribers.add(subscriber);
    const { t } = useI18n();
    const toast = useLaunchToast();
    const { isLiveUpdatesAvailable } = storeToRefs(useSignalRStore());

    const showReconnectInfoToast = toast(
        'warn',
        t('signalR.reconnecting.title'),
        undefined,
        'sticky',
        {
            group: 'signalr',
        }
    );

    const showReconnectFailureToast = toast(
        'error',
        t('signalR.disconnected.title'),
        t('signalR.disconnected.message'),
        'sticky',
        { group: 'signalr' }
    );

    const toasts = useToast();
    const { isAuthenticated } = storeToRefs(useAuthStore());

    let connection: HubConnection | null = null;
    const reconnectListener = () => onReconnected?.();

    const notificationHandler = <M extends keyof NotificationTypeMap>(
        newMethod: M,
        raw: NotificationTypeMap[M]
    ) => {
        if (!notificationType.includes(newMethod)) {
            return;
        }
        onNotificationReceived(raw);
    };

    const removeNotificationHandler = () => {
        if (!connection) {
            return;
        }
        connection.off(METHOD_NAME, notificationHandler);
    };

    const subscribeToConnection = (currentConnection: HubConnection) => {
        ensureFallbackHandler(currentConnection, METHOD_NAME);
        currentConnection.off(METHOD_NAME, notificationHandler);
        currentConnection.on(METHOD_NAME, notificationHandler);

        let reconnectListeners = signalRReconnectListeners.get(currentConnection);
        if (!reconnectListeners) {
            reconnectListeners = new Set();
            signalRReconnectListeners.set(currentConnection, reconnectListeners);
        }
        if (onReconnected) {
            reconnectListeners.add(reconnectListener);
        }
        if (signalRReconnectHandlers.has(currentConnection)) {
            return;
        }
        signalRReconnectHandlers.add(currentConnection);
        currentConnection.onreconnecting(() => {
            showReconnectInfoToast();
            isLiveUpdatesAvailable.value = false;
        });
        currentConnection.onreconnected(() => {
            resetSignalRReconnectRetryLimitReached();
            toasts.removeGroup('signalr');
            isLiveUpdatesAvailable.value =
                isAuthenticated.value && currentConnection.state === HubConnectionState.Connected;
            if (isLiveUpdatesAvailable.value) {
                signalRReconnectListeners.get(currentConnection)?.forEach(listener => listener());
            }
        });
        currentConnection.onclose(() => {
            isLiveUpdatesAvailable.value = false;
            if (!hasSignalRReconnectRetryLimitReached()) {
                return;
            }
            toasts.removeGroup('signalr');
            showReconnectFailureToast();
        });
    };

    watch(
        isAuthenticated,
        async (_authenticated, _previous, onCleanup) => {
            let active = true;
            const isSubscriptionActive = () => active && isAuthenticated.value;
            let subscribedConnection: HubConnection | null = null;
            onCleanup(() => {
                active = false;
                subscribedConnection?.off(METHOD_NAME, notificationHandler);
                if (subscribedConnection && onReconnected) {
                    signalRReconnectListeners.get(subscribedConnection)?.delete(reconnectListener);
                }
            });
            if (!isAuthenticated.value) {
                isLiveUpdatesAvailable.value = false;
                removeNotificationHandler();
                await stopSignalR();
                connection = null;
                return;
            }

            try {
                connection = await getSignalRConnection();
                if (!isSubscriptionActive() || !connection) {
                    return;
                }
                const currentConnection = connection;
                subscribedConnection = currentConnection;
                subscribeToConnection(currentConnection);

                const started = await startSignalR();
                if (!isSubscriptionActive()) {
                    return;
                }
                isLiveUpdatesAvailable.value =
                    started && currentConnection.state === HubConnectionState.Connected;
                if (isLiveUpdatesAvailable.value) {
                    resetSignalRReconnectRetryLimitReached();
                    toasts.removeGroup('signalr');
                }
            } catch (error) {
                if (isSubscriptionActive()) {
                    isLiveUpdatesAvailable.value = false;
                    showReconnectFailureToast();
                    throw error;
                }
            }
        },
        { immediate: true }
    );

    onUnmounted(async () => {
        removeNotificationHandler();
        subscribers.delete(subscriber);
        if (subscribers.size === 0) {
            isLiveUpdatesAvailable.value = false;
            await stopSignalR();
        }
    });
}
